import React, { useEffect, useState } from 'react';
import styles from './AdminShared.module.scss';
import { ParticipantAttributeTypeCreateDto, ParticipantAttributeTypeDto } from '../../../types/admin.types';
import {
    createParticipantAttributeType,
    deleteParticipantAttributeType,
    fetchParticipantAttributeTypes,
    updateParticipantAttributeType
} from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';

const emptyForm: ParticipantAttributeTypeCreateDto = {
    Name: '',
    Description: ''
};

const AdminParticipantAttributes = () => {
    const { getUserInfo } = useUserState();
    const [items, setItems] = useState<ParticipantAttributeTypeDto[]>([]);
    const [form, setForm] = useState<ParticipantAttributeTypeCreateDto>(emptyForm);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const token = getUserInfo()?.token;

    const loadItems = async () => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const response = await fetchParticipantAttributeTypes(token);
            setItems(response);
        } catch (error) {
            setErrorMessage('Unable to load participant attributes.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadItems();
    }, []);

    const resetForm = () => {
        setForm(emptyForm);
        setSelectedId(null);
    };

    const handleEdit = (item: ParticipantAttributeTypeDto) => {
        setSelectedId(item.Id);
        setForm({
            Name: item.Name,
            Description: item.Description
        });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage(null);
        try {
            if (selectedId) {
                await updateParticipantAttributeType({ ...form, Id: selectedId }, token);
            } else {
                await createParticipantAttributeType(form, token);
            }
            await loadItems();
            resetForm();
        } catch (error) {
            setErrorMessage('Unable to save participant attribute.');
        }
    };

    const handleDelete = async (id: string) => {
        setErrorMessage(null);
        try {
            await deleteParticipantAttributeType(id, token);
            if (selectedId === id) {
                resetForm();
            }
            await loadItems();
        } catch (error) {
            setErrorMessage('Unable to delete participant attribute.');
        }
    };

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1>Participant Attributes</h1>
                    <p>Manage participant attribute types used for fighter bios.</p>
                </div>
            </header>

            {errorMessage ? <div className={styles.muted}>{errorMessage}</div> : null}

            <div className={styles.content}>
                <section className={styles.panel}>
                    <h2>Attribute Types</h2>
                    {isLoading ? (
                        <div className={styles.muted}>Loading attributes...</div>
                    ) : items.length === 0 ? (
                        <div className={styles.empty}>No attribute types yet.</div>
                    ) : (
                        items.map((item) => (
                            <div key={item.Id} className={styles.listItem}>
                                <div>
                                    <strong>{item.Name}</strong>
                                    <div className={styles.muted}>{item.Description}</div>
                                </div>
                                <div className={styles.rowActions}>
                                    <button type="button" className={styles.secondary} onClick={() => handleEdit(item)}>Edit</button>
                                    <button type="button" className={styles.danger} onClick={() => handleDelete(item.Id)}>Delete</button>
                                </div>
                            </div>
                        ))
                    )}
                </section>

                <section className={styles.panel}>
                    <h2>{selectedId ? 'Edit Attribute' : 'New Attribute'}</h2>
                    <form onSubmit={handleSubmit}>
                        <div className={styles.field}>
                            <label htmlFor="attribute-name">Name</label>
                            <input
                                id="attribute-name"
                                value={form.Name}
                                onChange={(event) => setForm({ ...form, Name: event.target.value })}
                                maxLength={200}
                                required
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="attribute-description">Description</label>
                            <textarea
                                id="attribute-description"
                                value={form.Description}
                                onChange={(event) => setForm({ ...form, Description: event.target.value })}
                                maxLength={2000}
                            />
                        </div>
                        <div className={styles.actions}>
                            <button className={styles.primary} type="submit">
                                {selectedId ? 'Update Attribute' : 'Create Attribute'}
                            </button>
                            {selectedId ? (
                                <button type="button" className={styles.secondary} onClick={resetForm}>Cancel</button>
                            ) : null}
                        </div>
                    </form>
                </section>
            </div>
        </div>
    );
};

export default AdminParticipantAttributes;
